import React, { ReactNode } from "react";
import Modal from "../../../components/common/Modal";
import DownloadIcon from "../../../assets/svg-tsx/DownloadIcon";
import WatchIcon from "../../../assets/svg-tsx/WatchIcon";
import PlayIcon from "../../../assets/svg-tsx/PlayIcon";

interface FileData {
  url?: string;
  value?: string;
}

interface ChangeData {
  field_name: string;
  old_data: FileData | null;
  new_data: FileData | null;
}

const ViewChangesModal = ({
  setShowViewChangesModal,
  projectChangeRequestData,
}: {
  setShowViewChangesModal: (value: boolean) => void;
  projectChangeRequestData: any;
}) => {
  const projectChanges = projectChangeRequestData || [];

  const findExtension = (fileName: string = "") => {
    return fileName.split(".").pop()?.toLowerCase() || "";
  };

  const handleDownload = async (data: FileData, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!data?.url) return;

    const fullUrl = `${process.env.REACT_APP_BUCKET}/${data.url}`;

    try {
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.value || "download";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const renderFileActions = (data: FileData | null) => {
    if (!data?.url) return null;

    const fullUrl = `${process.env.REACT_APP_BUCKET}/${data.url}`;
    const extension = findExtension(data.value || data.url);
    const isVideo = extension === "mp4";

    return (
      <div className="flex gap-2 items-center">
        <div
          className="cursor-pointer hover:opacity-80"
          onClick={(e) => handleDownload(data, e)}
        >
          <DownloadIcon color="gray" />
        </div>
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="cursor-pointer hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
        >
          {isVideo ? <PlayIcon color="gray" /> : <WatchIcon color="gray" />}
        </a>
      </div>
    );
  };

  const renderFileCell = (data: FileData | null): ReactNode => {
    if (!data) return "-";
    return (
      <div className="flex justify-between items-center gap-4">
        <span className="break-all">{data.value || "-"}</span>
        {renderFileActions(data)}
      </div>
    );
  };

  const isMediaField = (fieldName: string) => {
    return fieldName === "Photos and Videos" || fieldName === "Internal Files";
  };

  const renderCellContent = (
    item: ChangeData,
    isOldData: boolean
  ): ReactNode => {
    const data = isOldData ? item.old_data : item.new_data;

    if (isMediaField(item.field_name)) {
      return renderFileCell(data);
    }

    return (data?.value || data || "-") as string;
  };

  return (
    <div>
      <Modal
        header="Project changes"
        clickOutsideToClose
        handleCancel={() => {
          setShowViewChangesModal(false);
        }}
      >
        <div className="w-full overflow-y-auto custom-scrollbar h-[33vh]">
          <table className="w-full">
            <thead className="bg-[#ECECEC80] text-[#242424] border-[#ECECEC80] font-gilroy-semi-bold font-normal text-sm leading-4">
              <tr>
                <th className="w-[11rem] py-3 px-4 text-left border-[0.3px] border-[#ECECEC]">
                  Type
                </th>
                <th className="py-3 px-4 text-left border-[0.3px] border-[#ECECEC]">
                  Old Data
                </th>
                <th className="py-3 px-4 text-left border-[0.3px] border-[#ECECEC]">
                  New Data
                </th>
              </tr>
            </thead>
            <tbody className="font-gilroy-medium font-normal text-[#242424] text-sm leading-4">
              {projectChanges.map((item: ChangeData, index: number) => (
                <tr key={index}>
                  <td className="py-2 px-4 border-[0.3px] border-t-0 border-[#ECECEC] align-top">
                    {item.field_name || "-"}
                  </td>
                  <td className="py-2 px-4 border-[0.3px] border-t-0 border-[#ECECEC] align-top">
                    {renderCellContent(item, true)}
                  </td>
                  <td className="py-2 px-4 border-[0.3px] border-t-0 border-[#ECECEC] align-top">
                    {renderCellContent(item, false)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default ViewChangesModal;
